import dayjs from 'dayjs';
import DOMPurify from 'dompurify';

export interface BadgeData {
  visitorType: string;
  visitorName: string;
  visitorCompany: string;
  hostLabel: string;
  hostName: string;
  orgName: string;
  visitDate: string;
}

export async function printBadge(badgeData: BadgeData): Promise<void> {
  try {

    const response = await fetch('/visitor-badge.html');
    if (!response.ok) {
      throw new Error('Failed to load badge template');
    }
    let templateHtml = await response.text();
    const normalizedVisitorType = (badgeData.visitorType || '').toLocaleUpperCase();

    // Sanitize all user-supplied data to prevent XSS attacks (ACC-2026-1904)
    const renderedTemplateHtml = templateHtml
      .replaceAll('VISITOR TYPE', sanitizeText(normalizedVisitorType))
      .replaceAll('VISITOR FULL NAME', sanitizeText(badgeData.visitorName))
      .replaceAll('Visitor Company', sanitizeText(badgeData.visitorCompany))
      .replaceAll('HOST LABEL', sanitizeText(badgeData.hostLabel))
      .replaceAll('HOST NAME', sanitizeText(badgeData.hostName))
      .replaceAll('Org Name', sanitizeText(badgeData.orgName))
      .replaceAll('YYYY-MM-DD', sanitizeText(badgeData.visitDate));

    // Security: Use Blob URL instead of srcdoc to create a separate origin
    // This prevents XSS from accessing parent window's localStorage/cookies
    // even if sanitization is bypassed
    const blob = new Blob([renderedTemplateHtml], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.pointerEvents = 'none';
    iframe.style.border = '0';
    iframe.setAttribute('title', 'Badge Print');
    // Blob URLs create a separate origin, so sandbox is not needed
    // and print() will work reliably
    iframe.src = blobUrl;

    document.body.appendChild(iframe);

    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
      // Clean up blob URL to free memory
      URL.revokeObjectURL(blobUrl);
    };

    // Wait for iframe to load before printing
    iframe.onload = () => {
      try {
        setTimeout(() => {
          iframe.contentWindow?.focus();

          setTimeout(() => {
            iframe.contentWindow?.print();
          }, 100);

          setTimeout(cleanup, 1000);
        }, 500);
      } catch (error) {
        console.error('Error during print:', error);
        cleanup();
      }
    };

    // Fallback cleanup in case onload never fires
    setTimeout(() => {
      if (document.body.contains(iframe)) {
        console.warn('Badge print iframe load timeout, forcing cleanup');
        cleanup();
      }
    }, 10000);

  } catch (error) {
    console.error('Error printing badge:', error);
    throw error;
  }
}

export function formatVisitorName(firstName: string | null, middleName: string | null, lastName: string | null): string {
  const capitalizeFirst = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return '';
    return trimmedValue.charAt(0).toUpperCase() + trimmedValue.slice(1);
  };

  const parts = [firstName, middleName, lastName]
    .filter(Boolean)
    .map((part) => capitalizeFirst(part as string))
    .filter(Boolean);

  return parts.join(' ') || 'Unknown Visitor';
}

export function formatVisitDate(dateString: string | null): string {
  if (!dateString) return new Date().toISOString().split('T')[0];
  try {
    return dayjs(dateString).format('YYYY-MM-DD');
  } catch {
    return new Date().toISOString().split('T')[0];
  }
}

/**
 * Sanitize user-supplied text to prevent XSS attacks
 * Uses DOMPurify to strip all HTML tags, leaving only safe text content
 * Addresses ACC-2026-1904: prevents malicious HTML/JS injection in badge printing
 * @param text - User-supplied string that may contain HTML
 * @returns Sanitized text safe for insertion into HTML
 */
function sanitizeText(text: string): string {
  // Strip all HTML tags - only plain text is allowed in badge fields
  return DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [],
    KEEP_CONTENT: true,
  });
}
